"use client";

import { createContext, useContext, useState, ReactNode } from "react";
import { startOfMonth, endOfDay, format } from "date-fns";

type DateRangeType = "month" | "custom" | "3months" | "total" | "rolling12";

interface DashboardStateContextType {
    // Dashboard Specific State
    dateRange: DateRangeType;
    setDateRange: (range: DateRangeType) => void;
    customStart: string;
    setCustomStart: (date: string) => void;
    customEnd: string;
    setCustomEnd: (date: string) => void;
    chartMode: "factures" | "devis";
    setChartMode: (mode: "factures" | "devis") => void;

    // Reports Specific State
    reportsDateRange: DateRangeType;
    setReportsDateRange: (range: DateRangeType) => void;
    reportsCustomStart: string;
    setReportsCustomStart: (date: string) => void;
    reportsCustomEnd: string;
    setReportsCustomEnd: (date: string) => void;
}

const DashboardStateContext = createContext<DashboardStateContextType | undefined>(undefined);

export function DashboardStateProvider({ children }: { children: ReactNode }) {
    // Dashboard Defaults (Persisted)
    const [dateRange, setDateRange] = useState<DateRangeType>(() => {
        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem("dashboard_date_range");
            if (stored) return stored as DateRangeType;
        }
        return "3months"; // Default to 3 months for better data visibility
    });

    // Persist effect
    const setDateRangeWithPersistence = (range: DateRangeType) => {
        setDateRange(range);
        if (typeof window !== 'undefined') {
            localStorage.setItem("dashboard_date_range", range);
        }
    };

    const [customStart, setCustomStart] = useState<string>(format(startOfMonth(new Date()), "yyyy-MM-dd"));
    const [customEnd, setCustomEnd] = useState<string>(format(endOfDay(new Date()), "yyyy-MM-dd"));
    const [chartMode, setChartMode] = useState<"factures" | "devis">("factures");

    // Reports Defaults
    //
    // « rolling12 » — l'année glissante — est le défaut, à la première ouverture
    // comme après un rechargement. Il n'est délibérément PAS persisté : la page
    // des rapports s'ouvre toujours sur les douze derniers mois, quel que soit
    // le filtre choisi la fois d'avant.
    const [reportsDateRange, setReportsDateRange] = useState<DateRangeType>("rolling12");
    const [reportsCustomStart, setReportsCustomStart] = useState<string>(format(startOfMonth(new Date()), "yyyy-MM-dd"));
    const [reportsCustomEnd, setReportsCustomEnd] = useState<string>(format(endOfDay(new Date()), "yyyy-MM-dd"));

    return (
        <DashboardStateContext.Provider value={{
            dateRange, setDateRange: setDateRangeWithPersistence,
            customStart, setCustomStart,
            customEnd, setCustomEnd,
            chartMode, setChartMode,

            reportsDateRange, setReportsDateRange,
            reportsCustomStart, setReportsCustomStart,
            reportsCustomEnd, setReportsCustomEnd
        }}>
            {children}
        </DashboardStateContext.Provider>
    );
}

export function useDashboardState() {
    const context = useContext(DashboardStateContext);
    if (context === undefined) {
        throw new Error("useDashboardState must be used within a DashboardStateProvider");
    }
    return context;
}
